import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  CaretLeft, Gear, FloppyDisk, ArrowClockwise, ShieldSlash,
  InstagramLogo, TwitterLogo, TiktokLogo, Globe, PaperPlaneTilt,
  CheckCircle, CurrencyDollar,
} from "@phosphor-icons/react";
import apiClient from "../../lib/api";
import AdminSection from "../../components/admin/AdminSection";
import IosToggle from "../../components/IosToggle";
import { toast } from "sonner";

export default function AdminConfigPage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [runSuspend, setRunSuspend] = useState(false);
  const [testEmailTarget, setTestEmailTarget] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [emailLogs, setEmailLogs] = useState(null);
  const [loadingLogs, setLoadingLogs] = useState(false);

  useEffect(() => {
    apiClient.get("/admin/config")
      .then(r => setConfig(r.data))
      .catch(() => toast.error("Error al cargar configuración"))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await apiClient.put("/admin/config", config);
      setConfig(r.data);
      toast.success("Configuración guardada");
    } catch {
      toast.error("Error al guardar");
    }
    setSaving(false);
  };

  const runAutoSuspend = async () => {
    setRunSuspend(true);
    try {
      const r = await apiClient.post("/admin/run-suspension");
      if (r.data.suspended > 0) {
        toast.success(`${r.data.suspended} cuenta(s) suspendidas`);
      } else {
        toast.info("Sin cuentas pendientes de suspensión");
      }
    } catch {
      toast.error("Error al ejecutar suspensión");
    }
    setRunSuspend(false);
  };

  const sendTestEmail = async () => {
    if (!testEmailTarget || !testEmailTarget.includes("@")) {
      toast.error("Escribe un email válido para la prueba");
      return;
    }
    setSendingTest(true);
    try {
      const r = await apiClient.post("/admin/send-test-email", { type: "welcome", email: testEmailTarget });
      toast.success(`Email de prueba enviado a ${r.data.to}`);
      // Refresh logs after sending
      loadEmailLogs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al enviar email de prueba");
    }
    setSendingTest(false);
  };

  const loadEmailLogs = async () => {
    setLoadingLogs(true);
    try {
      const r = await apiClient.get("/admin/email-logs?limit=20");
      setEmailLogs(r.data);
    } catch {
      toast.error("Error al cargar historial de emails");
    }
    setLoadingLogs(false);
  };

  const set = (path, value) => {
    setConfig(prev => {
      const next = { ...prev };
      const keys = path.split(".");
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) {
        obj[keys[i]] = { ...obj[keys[i]] };
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  if (loading) return (
    <div className="max-w-3xl mx-auto px-4 lg:px-6 pt-8 space-y-4">
      {[...Array(4)].map((_, i) => <div key={i} className="surface rounded-xl h-24 animate-pulse" />)}
    </div>
  );

  return (
    <div className="min-h-screen bg-background pb-16">
      {/* Header */}
      <div className="border-b border-white/[0.06] bg-surface/50 backdrop-blur-sm sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 lg:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-zinc-500 hover:text-foreground transition-colors">
              <CaretLeft weight="bold" className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <Gear weight="duotone" className="w-5 h-5 text-yellow-400" />
              <span className="font-bold text-lg">Configuración</span>
            </div>
          </div>
          <button
            onClick={save}
            disabled={saving}
            data-testid="config-save-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-400 hover:bg-yellow-300 text-black text-sm font-bold transition-all disabled:opacity-50"
          >
            <FloppyDisk weight="bold" className="w-4 h-4" />
            {saving ? "Guardando..." : "Guardar cambios"}
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 lg:px-6 py-8 space-y-6">
        {/* General */}
        <AdminSection icon={Gear} title="General" subtitle="Datos básicos de la plataforma" color="amber">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-1.5 block">Nombre de la plataforma</label>
              <input
                data-testid="config-platform-name"
                className="input-field w-full"
                value={config?.platform_name || ""}
                onChange={e => set("platform_name", e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-1.5 block">Moneda</label>
              <select
                data-testid="config-currency"
                className="input-field w-full"
                value={config?.currency || "USD"}
                onChange={e => set("currency", e.target.value)}
              >
                <option value="USD">USD — Dólar</option>
                <option value="EUR">EUR — Euro</option>
                <option value="COP">COP — Peso colombiano</option>
                <option value="MXN">MXN — Peso mexicano</option>
              </select>
            </div>
          </div>
          <div className="flex items-center justify-between p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
            <div>
              <div className="text-sm font-semibold">Registro público</div>
              <div className="text-xs text-zinc-500 mt-0.5">Permitir a nuevos usuarios registrarse</div>
            </div>
            <IosToggle
              testid="config-toggle-registration"
              checked={!!config?.allow_public_registration}
              onChange={(v) => set("allow_public_registration", v)}
            />
          </div>
        </AdminSection>

        {/* Precios */}
        <AdminSection icon={CurrencyDollar} title="Precios y planes" subtitle="Lo que pagan tus clientes" color="emerald">
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-1.5 block">Plan semanal ($)</label>
              <input
                data-testid="config-price-weekly"
                type="number"
                className="input-field w-full"
                value={config?.pricing?.weekly || 50}
                onChange={e => set("pricing.weekly", parseFloat(e.target.value))}
              />
            </div>
            <div>
              <label className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-1.5 block">Plan mensual ($)</label>
              <input
                data-testid="config-price-monthly"
                type="number"
                className="input-field w-full"
                value={config?.pricing?.monthly || 160}
                onChange={e => set("pricing.monthly", parseFloat(e.target.value))}
              />
            </div>
            <div>
              <label className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-1.5 block">Días de trial</label>
              <input
                data-testid="config-trial-days"
                type="number"
                className="input-field w-full"
                value={config?.pricing?.trial_days || 3}
                onChange={e => set("pricing.trial_days", parseInt(e.target.value))}
              />
            </div>
          </div>
          <p className="text-xs text-zinc-600">Nota: Los cambios de precio aplican a nuevas suscripciones. Stripe requiere actualización manual del price_id para reflejar el nuevo monto.</p>
        </AdminSection>

        {/* Suspensión automática */}
        <AdminSection icon={ShieldSlash} title="Suspensión automática" subtitle="Cuentas con suscripción vencida" color="orange">
          <div className="flex items-center justify-between p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
            <div>
              <div className="text-sm font-semibold">Auto-suspensión activa</div>
              <div className="text-xs text-zinc-500 mt-0.5">Suspender usuarios con suscripción vencida</div>
            </div>
            <IosToggle
              testid="config-toggle-autosuspend"
              checked={!!config?.auto_suspension?.enabled}
              onChange={(v) => set("auto_suspension.enabled", v)}
            />
          </div>
          <div>
            <label className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-1.5 block">
              Días de gracia después del vencimiento
            </label>
            <input
              data-testid="config-suspension-days"
              type="number"
              className="input-field w-full max-w-xs"
              value={config?.auto_suspension?.days_after_expiry || 7}
              onChange={e => set("auto_suspension.days_after_expiry", parseInt(e.target.value))}
            />
          </div>
          <div className="pt-2">
            <button
              onClick={runAutoSuspend}
              disabled={runSuspend}
              data-testid="config-run-suspension-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-500/10 border border-orange-500/20 text-orange-400 hover:bg-orange-500/20 transition-all text-sm font-semibold disabled:opacity-50"
            >
              <ShieldSlash className="w-4 h-4" />
              {runSuspend ? "Ejecutando..." : "Ejecutar suspensión ahora"}
            </button>
          </div>
        </AdminSection>

        {/* Notificaciones */}
        <AdminSection icon={PaperPlaneTilt} title="Notificaciones por email" subtitle="Prueba y revisa el envío de correos" color="sky">

          {/* Test email tool */}
          <div className="p-4 rounded-xl border border-yellow-500/20 bg-yellow-500/5 mb-2">
            <div className="flex items-center gap-2 mb-2">
              <PaperPlaneTilt className="w-4 h-4 text-yellow-400" weight="fill" />
              <span className="text-sm font-bold text-yellow-300">Probar entrega de correo</span>
            </div>
            <p className="text-xs text-zinc-400 mb-3">Envía un email de prueba para verificar que los correos llegan correctamente. Si no llega, busca en <strong>Spam / Correo no deseado</strong>.</p>
            <div className="flex gap-2">
              <input
                data-testid="config-test-email-input"
                type="email"
                placeholder="tu@email.com"
                value={testEmailTarget}
                onChange={e => setTestEmailTarget(e.target.value)}
                onKeyDown={e => e.key === "Enter" && sendTestEmail()}
                className="flex-1 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 outline-none focus:ring-1 focus:ring-yellow-400/40"
                style={{ background: "#0A0A0C", border: "1px solid rgba(255,255,255,0.1)" }}
              />
              <button
                data-testid="config-send-test-email-btn"
                onClick={sendTestEmail}
                disabled={sendingTest}
                className="px-4 py-2 rounded-lg text-sm font-bold text-black transition-all hover:brightness-110 disabled:opacity-50 flex items-center gap-1.5"
                style={{ background: "#D4AF37" }}
              >
                {sendingTest ? <ArrowClockwise className="w-4 h-4 animate-spin" /> : <PaperPlaneTilt className="w-4 h-4" />}
                {sendingTest ? "Enviando..." : "Enviar"}
              </button>
            </div>

          {/* Email delivery history */}
          <div className="mt-3">
            <button
              data-testid="config-view-email-logs-btn"
              onClick={loadEmailLogs}
              disabled={loadingLogs}
              className="flex items-center gap-2 text-xs font-semibold text-zinc-400 hover:text-white transition-colors"
            >
              <ArrowClockwise className={`w-3.5 h-3.5 ${loadingLogs ? "animate-spin" : ""}`} />
              {emailLogs ? "Actualizar historial de emails" : "Ver historial de envíos"}
            </button>
            {emailLogs && (
              <div className="mt-3 rounded-xl overflow-hidden border border-white/[0.06]">
                <div className="flex items-center justify-between px-4 py-2.5 bg-white/[0.03]">
                  <span className="text-xs font-bold text-zinc-300">Últimos {emailLogs.logs.length} emails</span>
                  <div className="flex gap-3 text-xs">
                    <span className="text-emerald-400">{emailLogs.total - emailLogs.failed} enviados</span>
                    {emailLogs.failed > 0 && <span className="text-red-400 font-bold">{emailLogs.failed} fallidos</span>}
                  </div>
                </div>
                <div className="divide-y divide-white/[0.04] max-h-64 overflow-y-auto">
                  {emailLogs.logs.map((log, i) => (
                    <div key={i} className="flex items-start gap-3 px-4 py-2.5">
                      {log.success
                        ? <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" weight="fill" />
                        : <span className="w-4 h-4 rounded-full bg-red-500 shrink-0 mt-0.5 flex items-center justify-center text-[9px] font-bold text-white">!</span>
                      }
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-semibold text-white truncate max-w-[180px]">{log.to}</span>
                          <span className="text-[10px] text-zinc-500 shrink-0">{new Date(log.sent_at).toLocaleString("es", { dateStyle: "short", timeStyle: "short" })}</span>
                        </div>
                        <div className="text-[11px] text-zinc-500 truncate">{log.subject}</div>
                        {!log.success && <div className="text-[11px] text-red-400 mt-0.5 break-words">{log.detail}</div>}
                      </div>
                    </div>
                  ))}
                  {emailLogs.logs.length === 0 && (
                    <div className="px-4 py-4 text-center text-xs text-zinc-500">No hay registros aún</div>
                  )}
                </div>
              </div>
            )}
          </div>
          </div>
          {[
            { key: "notifications.email_welcome", label: "Email de bienvenida", desc: "Al registrarse un nuevo usuario" },
            { key: "notifications.email_new_pick", label: "Email nueva jugada", desc: "Al publicar una jugada" },
            { key: "notifications.email_suspension", label: "Email de suspensión", desc: "Al suspender una cuenta" },
          ].map(({ key, label, desc }) => {
            const keys = key.split(".");
            const val = config?.[keys[0]]?.[keys[1]];
            return (
              <div key={key} className="flex items-center justify-between p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                <div>
                  <div className="text-sm font-semibold">{label}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{desc}</div>
                </div>
                <IosToggle
                  testid={`config-toggle-${keys[1]}`}
                  checked={!!val}
                  onChange={(v) => set(key, v)}
                />
              </div>
            );
          })}
          <div>
            <label className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-1.5 block">
              Días antes del vencimiento para recordatorio
            </label>
            <input
              data-testid="config-expiry-reminder-days"
              type="number"
              className="input-field w-full max-w-xs"
              value={config?.notifications?.email_expiry_reminder_days || 3}
              onChange={e => set("notifications.email_expiry_reminder_days", parseInt(e.target.value))}
            />
          </div>
        </AdminSection>

        {/* Redes sociales */}
        <AdminSection icon={Globe} title="Redes sociales" subtitle="Enlaces públicos de la plataforma" color="violet">
          {[
            { key: "social.instagram", label: "Instagram", icon: InstagramLogo, placeholder: "https://instagram.com/picksvault" },
            { key: "social.twitter", label: "Twitter / X", icon: TwitterLogo, placeholder: "https://twitter.com/picksvault" },
            { key: "social.tiktok", label: "TikTok", icon: TiktokLogo, placeholder: "https://tiktok.com/@picksvault" },
            { key: "social.facebook", label: "Facebook", icon: Globe, placeholder: "https://facebook.com/picksvault" },
          ].map(({ key, label, icon: Icon, placeholder }) => {
            const keys = key.split(".");
            return (
              <div key={key}>
                <label className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-1.5 flex items-center gap-1.5">
                  <Icon className="w-3.5 h-3.5" /> {label}
                </label>
                <input
                  data-testid={`config-social-${keys[1]}`}
                  className="input-field w-full"
                  placeholder={placeholder}
                  value={config?.social?.[keys[1]] || ""}
                  onChange={e => set(key, e.target.value)}
                />
              </div>
            );
          })}
        </AdminSection>

        {/* Sticky save */}
        <div className="flex justify-end pt-2">
          <button
            onClick={save}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 rounded-xl bg-yellow-400 hover:bg-yellow-300 text-black font-bold transition-all disabled:opacity-50"
          >
            <FloppyDisk weight="bold" className="w-4 h-4" />
            {saving ? "Guardando..." : "Guardar configuración"}
          </button>
        </div>
      </div>
    </div>
  );
}
